import { forwardRef, useEffect, useImperativeHandle, useRef, useState } from "react";
import { useInfiniteQuery } from "react-query";
import { useNavigate } from "react-router";
import styled from "styled-components";
import { v4 as uuidv4 } from "uuid";
import { axiosInstance } from "../../../api/axios";
import * as Library from "../../../utils/Library";
import { roleAccesse } from "../../../utils/roleManager";
import CardGridSkeleton from "../gridCards/animations/CardGridSkeleton";
import CompanyGridCard from "../gridCards/CompanyGridCard";
import { AppQueryClient } from "../../../api/queryClient";

/* #region  SCOPED CSS */
const StyledPage = styled.div`
    & {
        overflow-y: scroll;
        height: 100%;
        margin-right: -24px;
        margin-left: -24px;
        padding-left: 24px;
        padding-right: 17px;
    }
`;
/* #endregion */

export default forwardRef(function InfinityGridCompanyCards(props, ref) {
    const gridLayoutStyle = {
        display: "grid",
        gridTemplateColumns: `repeat(auto-fill, minmax(${props?.minColumnWidth || "395px"}, 1fr))`,
        gap: "24px"
    };

    /* #region STATES */
    const navigate = useNavigate();
    const scrollContainerRef = useRef(null);
    const [scroll, setScroll] = useState(true);
    const [pageLimit] = useState(8);
    const [queryKey, setQueryKey] = useState(props?.queryKey ?? uuidv4());
    const [minimumCardsFill, setminimumCardsFill] = useState(1); //props?.minimumCardsFill || pageLimit);

    useEffect(() => {
        if (props?.queryKey) setQueryKey(props?.queryKey);
    }, [props?.queryKey]);

    const filters = props?.filters || {};

    const {
        data,
        isFetching: isLoading,
        fetchNextPage,
        hasNextPage,
        isFetchingNextPage
    } = Library.usePostInfiniteQuery(
        queryKey,
        "getCompaniesData",
        {
            organizationID: localStorage.getItem("currentOrganizationID"),
            limit: pageLimit,
            ...filters
        },
        () => {
            setScroll(true);
        },
        true,
        "Company"
    );

    const pageList = data?.pages;
    //const totalCount = data?.pages[0]?.data?.totalCount;
    const nextDataCount = data?.pages[data?.pages.length - 1].data?.nextDataCount;
    /* #endregion */

    /* #region EFFECTS */
    useEffect(() => {
        handleScroll();

        setminimumCardsFill(getTotalCards() + 1);
    }, [data?.pages]);

    useEffect(() => {
        const scrollContainer = scrollContainerRef.current;
        if (scrollContainer && !isLoading && scroll && !isFetchingNextPage) {
            //console.log("scroll event added");
            scrollContainer.addEventListener("scroll", handleScroll);
        }

        return () => {
            //console.log("scroll event removed");
            scrollContainer?.removeEventListener("scroll", handleScroll);
        };
    }, [scrollContainerRef, isLoading, pageList, scroll, isFetchingNextPage, data?.pages]);
    /* #endregion */

    /* #region METHODS */
    const handleScroll = () => {
        const scrollContainer = scrollContainerRef.current;

        const scrolledRatio = scrollContainer.scrollTop / (scrollContainer.scrollHeight - scrollContainer.clientHeight);
        const isAtBottom = scrolledRatio >= 0.6;

        if (isAtBottom && !isLoading && hasNextPage && !isFetchingNextPage) {
            setScroll(false);
            fetchNextPage();
        }
    };

    const getTotalCards = () => {
        return pageList?.reduce((acc, it) => {
            return acc + it?.data?.result?.length;
        }, 0);
    };

    const handleReset = infiniteGridQuerykey => {
        scrollContainerRef.current.scrollTop = 0;
        setScroll(true);
        AppQueryClient.clear();

        const newQueryKeyTMP = [...infiniteGridQuerykey];
        newQueryKeyTMP["refresh"] = uuidv4();
        setQueryKey(newQueryKeyTMP);
    };
    /* #endregion */

    useImperativeHandle(ref, () => ({
        reset: handleReset
    }));

    return (
        <StyledPage className={`library-scrollable ${props?.className || ""}`} ref={scrollContainerRef}>
            {isLoading && !isFetchingNextPage ? (
                <div style={gridLayoutStyle}>
                    {Array.from({ length: pageLimit }, () => (
                        <CardGridSkeleton key={uuidv4()} height="417.28px" />
                    ))}
                </div>
            ) : (
                <div className="mb-24px" style={gridLayoutStyle}>
                    {pageList?.map(it => {
                        return it?.data?.result?.map(item => {
                            const editAccess = roleAccesse(props?.userRole, "Retail Brand Account Admin");
                            return (
                                <CompanyGridCard
                                    key={item?.ourID}
                                    image={item?.logoURL}
                                    titlePrefix="COMPANY"
                                    title={item?.name}
                                    id={item?.ourID}
                                    isCardEnabled={true}
                                    //isCardSuspended={status?.toLowerCase() === "suspended"} //TODO: needed to check suspended status?
                                    data={{
                                        createdBy: item?.createdBy,
                                        createdOn: item?.createdOn,
                                        registrationNumber: item?.registrationNumber,
                                        registrationDate: item?.registrationDate,
                                        taxNr: item?.taxNr,
                                        nrOfBrands: item?.nrOfBrands,
                                        nrOfStores: item?.nrOfStores,
                                        status: item?.status
                                    }}
                                    primaryButton={{
                                        title: "ADD COMPANY"
                                        /*onClick: () =>
                                            hasNavigationAccess(navigations, "companies") &&
                                            navigate("/companies/create") */
                                    }}
                                    secondaryButton={{
                                        title: "ADD USERS"
                                        /* onClick: () => {
                                            navigate("/companies/users");
                                        } */
                                    }}
                                    edit={{
                                        onClick: editAccess
                                            ? () => {
                                                  navigate(`/companies/edit/${item?._id}`);
                                              }
                                            : undefined
                                    }}
                                />
                            );
                        });
                    })}

                    {!isFetchingNextPage &&
                        minimumCardsFill - getTotalCards() > 0 &&
                        Array.from({ length: minimumCardsFill - getTotalCards() }, (_, index) => (
                            <CompanyGridCard key={index} isCardEnabled={false} />
                        ))}

                    {isFetchingNextPage &&
                        Array.from({ length: nextDataCount }, (_, index) => (
                            <CardGridSkeleton key={index} height="417.28px" />
                        ))}
                </div>
            )}
        </StyledPage>
    );
});

export const useInfiniteQueryData = (payload, onSuccess) => {
    //console.log("useInfiniteQueryData", payload.queryKey);

    return useInfiniteQuery({
        queryKey: [payload.queryKey],
        queryFn: async ({ pageParam = 1 }) => {
            const { data } = await axiosInstance.get(
                `${payload.api}?${payload.key}=${payload.value}&limit=${payload.pageLimit}&page=${pageParam}`
            );

            //console.log(">>>", data);
            return data;
        },

        getNextPageParam: (lastPage, pages) => {
            //console.log("pages", pages);

            return lastPage?.data?.nextPage;
        },
        onSuccess: onSuccess
    });
};
